import { useEffect, useRef, useState } from "react";
import { PackagePlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUpdateDielec } from "@/api/hooks/useDielce";
import { extractErrorMessage } from "@/api/client";
import { toast } from "@/components/ui/use-toast";
import { formatEur, normalizeNum, parseDecimal } from "@/lib/format";
import { MJ_VLASTNEJ, predajVlastnej, type MjVlastnej } from "@/lib/vlastnaPolozka";
import type { Dielec, DielecUpdate } from "@/api/schemas";

interface Polia {
  nazov: string;
  mnozstvo: string;
  dodavatel: string;
  mj: string;
  nakup: string;
  rezia: string;
  praca: string;
}

function zDielca(d: Dielec): Polia {
  return {
    nazov: d.nazov_strojovy ?? "",
    // Desatinné množstvo (KOLO 211); riadok bez neho mal množstvo v Ks
    mnozstvo: normalizeNum(d.vlastna_mnozstvo ?? d.pocet_ks ?? 1),
    dodavatel: d.vlastna_dodavatel ?? "",
    mj: d.vlastna_mj ?? "ks",
    nakup: normalizeNum(d.vlastna_nakup),
    rezia: normalizeNum(d.vlastna_rezia_pct),
    praca: normalizeNum(d.vlastna_praca),
  };
}

/** Čo sa od uloženého líši — len tie polia idú do PATCH. */
function zmeny(p: Polia, d: Dielec): DielecUpdate {
  const z = zDielca(d);
  const out: DielecUpdate = {};
  if (p.nazov !== z.nazov) out.nazov_strojovy = p.nazov || null;
  if (p.mnozstvo.trim() !== "" && Number(parseDecimal(p.mnozstvo)) !== Number(z.mnozstvo)) {
    out.vlastna_mnozstvo = parseDecimal(p.mnozstvo);
  }
  if (p.dodavatel !== z.dodavatel) out.vlastna_dodavatel = p.dodavatel.trim() || null;
  if (p.mj !== z.mj) out.vlastna_mj = p.mj as MjVlastnej;
  // Prázdna cena sa neposiela — server by ju vrátil 422 (cena je povinná)
  if (p.nakup.trim() !== "" && Number(parseDecimal(p.nakup)) !== Number(z.nakup)) {
    out.vlastna_nakup = parseDecimal(p.nakup);
  }
  if (p.rezia.trim() !== "" && Number(parseDecimal(p.rezia)) !== Number(z.rezia)) {
    out.vlastna_rezia_pct = parseDecimal(p.rezia);
  }
  // Práca (KOLO 216) — prázdna = bez práce
  if (Number(parseDecimal(p.praca) || 0) !== Number(z.praca || 0)) {
    out.vlastna_praca = p.praca.trim() === "" ? null : parseDecimal(p.praca);
  }
  return out;
}

/**
 * Detail vlastnej položky mimo cenníka (KOLO 210) — názov, množstvo, MJ,
 * dodávateľ, nákup za MJ, réžia a práca za MJ (KOLO 216). Ukladá sa sám
 * ako ostatné detaily (400 ms po poslednej zmene, pri prepnutí riadku hneď).
 */
export function VlastnaDetailBody({
  zakazkaId,
  dielec,
}: {
  zakazkaId: string;
  dielec: Dielec;
}): JSX.Element {
  const updateMutation = useUpdateDielec(zakazkaId);
  const [polia, setPolia] = useState<Polia>(() => zDielca(dielec));
  const nastav = (pole: keyof Polia) => (v: string) =>
    setPolia((p) => ({ ...p, [pole]: v }));

  // Rovnaký vzor ako KovanieDetailBody — rozpísaná zmena sa pri prepnutí
  // dielca odošle, nestratí
  const pendingRef = useRef<{ id: string; data: DielecUpdate } | null>(null);
  const mutateRef = useRef(updateMutation.mutate);
  useEffect(() => {
    mutateRef.current = updateMutation.mutate;
  });
  useEffect(() => {
    return () => {
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) mutateRef.current(pending);
    };
  }, [dielec.id]);
  useEffect(() => {
    setPolia(zDielca(dielec));
  }, [dielec]);
  useEffect(() => {
    const data = zmeny(polia, dielec);
    if (Object.keys(data).length === 0) {
      pendingRef.current = null;
      return undefined;
    }
    const pending = { id: dielec.id, data };
    pendingRef.current = pending;
    const t = setTimeout(() => {
      pendingRef.current = null;
      updateMutation.mutateAsync(pending).catch((err: unknown) => {
        toast({
          variant: "destructive",
          title: "Chyba pri ukladaní",
          description: extractErrorMessage(err),
        });
      });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dielec, polia]);

  const predaj = predajVlastnej(
    parseDecimal(polia.nakup),
    parseDecimal(polia.rezia),
    parseDecimal(polia.praca),
  );
  const mnozstvo = Number(parseDecimal(polia.mnozstvo)) || 0;

  const pole = (
    id: string,
    label: string,
    hodnota: string,
    onChange: (v: string) => void,
    trieda = "w-24",
  ): JSX.Element => (
    <div>
      <Label
        htmlFor={id}
        className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground"
      >
        {label}
      </Label>
      <Input
        id={id}
        value={hodnota}
        onChange={(e) => onChange(e.target.value)}
        inputMode={id === "vd-nazov" || id === "vd-dodavatel" ? undefined : "decimal"}
        className={`h-6 px-1.5 text-xs ${trieda}`}
      />
    </div>
  );

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border bg-card">
      <div className="flex shrink-0 flex-wrap items-start gap-x-4 gap-y-1 border-b bg-background-alt/40 px-4 py-1.5">
        <div>
          <div className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground">Kód</div>
          <div className="font-mono text-sm font-semibold leading-6">{dielec.kod ?? "—"}</div>
        </div>
        {pole("vd-nazov", "Názov", polia.nazov, nastav("nazov"), "w-56")}
        {pole("vd-mnozstvo", "Množstvo", polia.mnozstvo, nastav("mnozstvo"), "w-20")}
        <div>
          <Label
            htmlFor="vd-mj"
            className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground"
          >
            MJ
          </Label>
          <Select value={polia.mj} onValueChange={nastav("mj")}>
            <SelectTrigger id="vd-mj" className="h-6 w-20 px-1.5 text-xs" aria-label="Merná jednotka">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MJ_VLASTNEJ.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
        <h3 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
          <PackagePlus className="h-3 w-3" />
          Vlastná položka mimo cenníka
        </h3>
        <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
          {pole("vd-dodavatel", "Dodávateľ", polia.dodavatel, nastav("dodavatel"), "w-56")}
          {pole("vd-nakup", "Nákup / MJ (€)", polia.nakup, nastav("nakup"))}
          {pole("vd-rezia", "Réžia (%)", polia.rezia, nastav("rezia"), "w-16")}
          {pole("vd-praca", "Práca / MJ (€)", polia.praca, nastav("praca"))}
        </div>
        <p className="mt-2 text-xs" aria-live="polite">
          {predaj === null ? (
            <span className="text-destructive">Zadaj nákupnú cenu.</span>
          ) : (
            <>
              Predaj {formatEur(predaj)} / {polia.mj}
              {mnozstvo > 0 ? <> · riadok spolu <b>{formatEur(predaj * mnozstvo)}</b></> : null}
              <span className="text-muted-foreground"> (pred ziskom zostavy)</span>
            </>
          )}
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Do cenníka nepatrí — v súpise pre objednávky, nákladov a na Objednávkach
          je u dodávateľa z riadku.
        </p>
      </div>
    </div>
  );
}
